import axios, { AxiosResponse } from "axios";
import { cloneDeep } from "lodash";
import { fetchWithRetry } from "../util/api";
import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  ReactNode,
} from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { getNextMarketWindows } from "../util/algorithms";
import { positiveGreen, textMultiplier } from "../util/style";
import {
  User,
  Trade,
  ParsedTrade,
  SplitAdjustHistory,
  TradesByAccount,
  TradeTemplate,
  ChartParams,
  ChartDataResult,
  ChartDataMap,
  ZoomPortionIndex,
  RangeIntervalMap,
  IntervalOption,
  MostGranularIntervalMap,
  WindowSecondsByInterval,
  MarketTimes,
  MarketStatus,
  DisplayDataParsed,
  ConversionModes,
  CurrencyTicker,
  CutOffDateChart,
  HoldingInfo,
  AppContextValue,
  UseApiFetchParams,
  StockDataResponse,
} from "../types";

const AppContext = createContext<AppContextValue | undefined>(undefined);

interface AppProviderProps {
  children: ReactNode;
}

export const AppProvider: React.FC<AppProviderProps> = ({ children }) => {
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    if (location.pathname === "/") {
      navigate("/market");
    }
  }, [location, navigate]);

  const userTemplate: User = { username: "", watchlist: [], _id: 0 };
  const SESSION_TIMEOUT = 15 * 60 * 1000;
  const THROTTLE_INTERVAL = 1000;

  const [user, setUser] = useState<User>(() => {
    try {
      const sessionData = localStorage.getItem("tradeSession");
      if (sessionData) {
        const parsed = JSON.parse(sessionData);
        if (parsed.expiry && Date.now() < parsed.expiry && parsed.user) {
          const newSessionData = {
            user: parsed.user,
            expiry: Date.now() + SESSION_TIMEOUT,
          };
          localStorage.setItem("tradeSession", JSON.stringify(newSessionData));
          return parsed.user;
        }
        localStorage.removeItem("tradeSession");
      }
    } catch (e) {
      localStorage.removeItem("tradeSession");
    }
    return userTemplate;
  });

  const hasAutoSelectedTicker = useRef(false);

  const isLogged = useMemo(() => {
    return user?.username !== "";
  }, [user]);

  const lastSessionUpdate = useRef<number>(0);
  useEffect(() => {
    if (!isLogged) return;

    const resetSessionExpiry = () => {
      const now = Date.now();
      if (now - lastSessionUpdate.current < THROTTLE_INTERVAL) return;
      lastSessionUpdate.current = now;

      try {
        const sessionData = localStorage.getItem("tradeSession");
        if (sessionData) {
          const parsed = JSON.parse(sessionData);
          if (parsed.user) {
            const newSessionData = {
              user: parsed.user,
              expiry: now + SESSION_TIMEOUT,
            };
            localStorage.setItem(
              "tradeSession",
              JSON.stringify(newSessionData),
            );
          }
        }
      } catch (e) {
        // Ignore errors
      }
    };

    const events: string[] = [
      "mousedown",
      "mousemove",
      "touchstart",
      "click",
      "keydown",
      "scroll",
    ];
    events.forEach((event) =>
      window.addEventListener(event, resetSessionExpiry),
    );

    return () => {
      events.forEach((event) =>
        window.removeEventListener(event, resetSessionExpiry),
      );
    };
  }, [isLogged]);

  const handleLogout = () => {
    setTradesByAccount({});
    setUser(userTemplate);
    localStorage.removeItem("tradeSession");
  };
  const [showLoginModal, setShowLoginModal] = useState<boolean>(!isLogged);

  const clockStartTime = useRef<number>(Date.now());
  const prevAutoRefreshClock = useRef<number>(0);
  const [autoRefreshClock, setAutoRefreshClock] = useState<number>(0);

  useEffect(() => {
    const generalCount = setInterval(() => {
      setAutoRefreshClock(Date.now() - clockStartTime.current);
    }, 1000);
    return () => clearInterval(generalCount);
  }, []);

  const defaultInfoInterval = "1m";
  const defaultInfoRange = "1d";
  const seconds = 1000;
  const minuteToSeconds = 60;
  const hourToMinutes = 60;
  const marketConditionRefreshRate = 10 * seconds;
  const serverConditionRefreshRate = 1 * minuteToSeconds * seconds;
  const sequenceRefreshRate = 5000;
  const maxStartingRefreshToken =
    0.25 * hourToMinutes * (60 / sequenceRefreshRate) * seconds;
  const chartToBackgroundRefreshRatio = 6;

  useEffect(() => {
    const intervalId = setInterval(() => {
      setMarketTimes(getNextMarketWindows());
    }, marketConditionRefreshRate);
    return () => clearInterval(intervalId);
  }, [marketConditionRefreshRate]);

  const serverActive = useRef<boolean>(false);

  const healthCheckRunning = useRef<boolean>(false);
  const runServiceHealthCheck = useCallback(async () => {
    if (healthCheckRunning.current) return;
    healthCheckRunning.current = true;
    let result: AxiosResponse | undefined;
    try {
      result = await axios.get("/healthCheck/");
    } catch (err) {
      // Ignore
    } finally {
      healthCheckRunning.current = false;
      serverActive.current = !!(result?.status === 200);
    }
  }, []);

  // Run health check on initial mount
  useEffect(() => {
    runServiceHealthCheck();
  }, [runServiceHealthCheck]);

  useEffect(() => {
    if (autoRefreshClock > 0) {
      const prevBucket = Math.floor(
        prevAutoRefreshClock.current / serverConditionRefreshRate,
      );
      const currBucket = Math.floor(
        autoRefreshClock / serverConditionRefreshRate,
      );
      if (currBucket > prevBucket) {
        runServiceHealthCheck();
      }
    }
  }, [autoRefreshClock, runServiceHealthCheck, serverConditionRefreshRate]);

  const checkHealthIfServiceDown = useCallback(() => {
    if (!serverActive.current) {
      runServiceHealthCheck();
    }
  }, [runServiceHealthCheck]);

  const useApiFetch = <T,>({
    url,
    body,
    onSuccess,
    runIfTrue = [],
    deps = [],
  }: UseApiFetchParams<T>) => {
    const running = useRef<boolean>(false);

    let proceed = true;
    runIfTrue.forEach((eachCondition) => {
      if (!eachCondition) {
        proceed = false;
      }
    });

    useEffect(() => {
      const fetchData = async () => {
        if (running.current) return;
        running.current = true;

        try {
          const result = await fetchWithRetry<T>(url, body);
          onSuccess(result?.data);
        } catch (error) {
          runServiceHealthCheck();
          console.error(`API Error at ${url}:`, error);
        } finally {
          running.current = false;
        }
      };

      if (proceed) {
        fetchData();
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, deps);
  };

  const tickerListCalled = useRef<string[]>([]);
  const tickerListPosted = useRef<string[]>([]);
  const sequenceIsCalled = useRef<boolean>(false);
  const directIsCalled = useRef<boolean>(false);
  const pinInput = useRef<HTMLInputElement | null>(null);
  const lastUpdateTime = useRef<number>(Math.floor(Date.now() / 1000));
  const sequentialRunning = useRef<boolean>(false);
  const [isRunning, setIsRunning] = useState<boolean>(false);

  const getFont = useCallback((size: number): string => {
    return size * textMultiplier + "px";
  }, []);

  const privacyPassword = "999";
  const deletePassword = "admin";
  const allTickerString = "ALL/TICKER";
  const allAccountString = "ALL/ACCOUNT";

  const [refreshToken, setRefreshToken] = useState<number>(
    maxStartingRefreshToken,
  );
  const [resetIdleTrigger, setResetIdleTrigger] = useState<number>(0);
  const [blink, setBlink] = useState<boolean>(false);

  useEffect(() => {
    setBlink(true);
    setTimeout(() => {
      setBlink(false);
    }, 100);
  }, [refreshToken]);

  const [chartParams, setChartParams] = useState<ChartParams>({
    range: "5y",
    interval: "1d",
  });
  const [showCandleChart, setShowCandleChart] = useState<boolean>(false);
  const [showVolume, setShowVolume] = useState<boolean>(true);
  const [showWick, setShowWick] = useState<boolean>(true);
  const [showTrades, setShowTrades] = useState<boolean>(true);
  const [searchTickerChart, setSearchTickerChart] = useState<string>("");
  const [chartData, setChartData] = useState<ChartDataMap>({});

  const rangeOption2: RangeIntervalMap = useMemo(() => {
    return {
      "1d": ["1m", "2m", "5m", "15m", "30m", "60m", "90m"],
      "5d": ["1m", "2m", "5m", "15m", "30m", "60m", "90m"],
      "1mo": ["1d", "5d", "1wk"],
      "3mo": ["1d", "5d", "1wk"],
      "6mo": ["1d", "5d", "1wk", "1mo"],
      ytd: ["1d", "5d", "1wk", "1mo"],
      "1y": ["1d", "5d", "1wk", "1mo"],
      "2y": ["1d", "5d", "1wk", "1mo"],
      "5y": ["1d", "5d", "1wk", "1mo"],
      "10y": ["1d", "5d", "1wk", "1mo"],
      max: ["1mo"],
    };
  }, []);

  const getMostGranularInterval: MostGranularIntervalMap = {
    "1d": "1m",
    "5d": "1m",
    "1mo": "1d",
    "3mo": "1d",
    "6mo": "1d",
    "1y": "1d",
    "2y": "1d",
    "5y": "1d",
    "10y": "1d",
    ytd: "1d",
    max: "1mo",
  };

  const windowSecondsByInterval: WindowSecondsByInterval = {
    "1m": 30,
    "2m": 60,
    "5m": 150,
    "15m": 450,
    "30m": 900,
    "60m": 3600,
    "90m": 3600,
    "1d": 43200,
    "5d": 43200,
    ytd: 43200,
    "1wk": 86400 * 3,
    "1mo": 86400 * 15,
    "3mo": 86400 * 15,
    "6mo": 86400 * 15,
    "1y": 86400 * 30,
    "2y": 86400 * 30,
    "5y": 86400 * 30,
    "10y": 86400 * 30,
    max: 86400 * 30,
  };

  const intervalOption: IntervalOption[] = useMemo(() => {
    let tempArray: IntervalOption[] = [];
    Object.keys(rangeOption2).forEach((eachRangeKey) => {
      tempArray = [
        ...tempArray,
        ...(rangeOption2[eachRangeKey] as IntervalOption[]),
      ];
    });
    return Array.from(new Set(tempArray));
  }, [rangeOption2]);

  const [darkMode, setDarkMode] = useState<boolean>(true);
  const [isDraggingChart, setIsDraggingChart] = useState<boolean>(false);

  const [selectedTicker, setSelectedTicker] = useState<string>("AAPL");
  useEffect(() => {
    setSearchTickerChart(selectedTicker);
  }, [selectedTicker]);

  const [selectedAccount, setSelectedAccount] =
    useState<string>(allAccountString);
  const [selectedCurrency, setSelectedCurrency] = useState<string>("CAD");
  const [accountInfo, setAccountInfo] = useState<Record<string, unknown>>({});
  const [tradesByAccount, setTradesByAccount] = useState<TradesByAccount>({});
  const [tickerQueue, setTickerQueue] = useState<string[]>([]);
  const [expandChart, setExpandChart] = useState<boolean>(false);
  const [expandWatchlist, setExpandWatchlist] = useState<boolean>(false);
  const [splitAdjustHistory, setSplitAdjustHistory] =
    useState<SplitAdjustHistory>({});
  const [selectedTradePointDetail, setSelectedTradePointDetail] = useState<
    Record<string, unknown>
  >({});
  const [badTickerCallCount, setBadTickerCallCount] = useState<
    Record<string, number>
  >({});
  const [zoomPortionIndex, setZoomPortionIndex] = useState<ZoomPortionIndex>({
    min: 0,
    max: Number.MAX_SAFE_INTEGER,
  });

  const defaultWatchList: string[] = useMemo(() => {
    return [
      "AAPL",
      "AMD",
      "QQQ",
      "SPY",
      "MSFT",
      "SHOP",
      "BA",
      "AMZN",
      "META",
      "NVDA",
      "TSLA",
      "BAC",
      "DIS",
      "GOOGL",
      "VIXY",
      "WMT",
      "COST",
      "HD",
      "USDCAD=X",
    ];
  }, []);

  const [tickerWatchlistSearchInput, setTickerWatchlistSearchInput] =
    useState<string>("");

  const cutOffDateChart: CutOffDateChart = useMemo(() => {
    return {
      min: 1,
      max: Math.floor(Date.now() / 1000),
    };
  }, []);

  useEffect(() => {
    checkHealthIfServiceDown();
  }, [selectedTicker, chartParams, checkHealthIfServiceDown]);

  const [lastPriceActual, setLastPriceActual] = useState<
    Record<string, number>
  >({});
  const [testPrices, setTestPrices] = useState<Record<string, number>>({});

  const lastPrice: Record<string, number> = useMemo(() => {
    const tempObj: Record<string, number> = {};
    Object.keys(lastPriceActual).forEach((eachTicker) => {
      tempObj[`${eachTicker}`] = Number(
        testPrices[`${eachTicker}`]
          ? testPrices[`${eachTicker}`]
          : lastPriceActual[`${eachTicker}`],
      );
    });
    return tempObj;
  }, [lastPriceActual, testPrices]);

  const [marketClosingPrice, setMarketClosingPrice] = useState<
    Record<string, number>
  >({});
  const [prevClosePrice, setPrevClosePrice] = useState<Record<string, number>>(
    {},
  );
  const [longName, setLongName] = useState<Record<string, string>>({});
  const [volume, setVolume] = useState<Record<string, number>>({});
  const [marketTimes, setMarketTimes] = useState<MarketTimes>(() =>
    getNextMarketWindows(),
  );

  const unrealizedTickersPrice: Record<string, number> = useMemo(() => {
    return Object.keys(chartData).reduce(
      (acc: Record<string, number>, eachKey) => {
        const targetIndex = chartData[eachKey]?.timestamp?.reduce(
          (accIdx: number, timestamp: number, idx: number) => {
            if (timestamp < cutOffDateChart.max) {
              if (chartData[eachKey]?.indicators?.quote?.[0]?.close?.[idx]) {
                accIdx = idx;
              }
            }
            return accIdx;
          },
          0,
        );
        if (lastPrice[eachKey.split("_")[0]]) {
          acc[chartData[eachKey].meta.symbol] =
            lastPrice[eachKey.split("_")[0]];
        } else if (
          chartData[eachKey]?.indicators?.quote?.[0]?.close?.[targetIndex]
        ) {
          acc[chartData[eachKey].meta.symbol] = chartData[eachKey]?.indicators
            ?.quote?.[0]?.close?.[targetIndex] as number;
        } else if (Number(chartData[eachKey]?.meta?.regularMarketPrice)) {
          acc[chartData[eachKey].meta.symbol] = chartData[eachKey]?.meta
            ?.regularMarketPrice as number;
        }

        return acc;
      },
      {},
    );
  }, [chartData, cutOffDateChart, lastPrice]);

  const displayDataParsed: DisplayDataParsed = useMemo(() => {
    const parseTicker = (
      ticker: string,
      trades: Record<string, Trade[]>,
    ): ParsedTrade[] => {
      const tempArray: ParsedTrade[] = [];
      const tradeChronoSorted = cloneDeep(trades[ticker])?.sort((a, b) => {
        return (
          Math.floor(new Date(a.date).getTime() / 1000) -
          Math.floor(new Date(b.date).getTime() / 1000)
        );
      });

      tradeChronoSorted?.forEach((trade, idx) => {
        const tradeDateUnix = Math.floor(new Date(trade.date).getTime() / 1000);
        let effectiveAdjustment = 1;

        if (splitAdjustHistory[ticker]) {
          Object.keys(splitAdjustHistory[ticker])?.forEach((eachSplitKey) => {
            if (splitAdjustHistory[ticker][eachSplitKey].date < tradeDateUnix) {
              effectiveAdjustment *=
                splitAdjustHistory[ticker][eachSplitKey].numerator /
                splitAdjustHistory[ticker][eachSplitKey].denominator;
            }
          });
        }

        const maxCurrentAdjust = splitAdjustHistory[ticker]
          ? Object.keys(splitAdjustHistory[ticker])?.reduce(
              (acc, eachSplitKey) =>
                (acc *=
                  splitAdjustHistory[ticker][eachSplitKey].numerator /
                  splitAdjustHistory[ticker][eachSplitKey].denominator),
              1,
            )
          : 1;
        const effectiveAdjustMultiple =
          (maxCurrentAdjust || 1) / parseFloat(String(effectiveAdjustment));

        const adjustedShare = trade.share * effectiveAdjustMultiple;
        const adjustedPrice =
          parseFloat(String(trade.price)) / effectiveAdjustMultiple;

        function roundToDecimals(value: number, decimals: number): number {
          const factor = Math.pow(10, decimals);
          return Math.round(value * factor) / factor;
        }

        const currentHolding =
          idx > 0
            ? adjustedShare +
              roundToDecimals(tempArray[idx - 1].currentHolding, 12)
            : adjustedShare;

        const originalShareCount =
          idx > 0 ? tempArray[idx - 1].currentHolding : 0;
        const resultingShareCount =
          idx > 0 ? originalShareCount + adjustedShare : adjustedShare;
        const originalCostBasis =
          idx > 0 ? tempArray[idx - 1].resultingCostBasis || 0 : 0;

        let resultingCostBasis = 0;
        if (
          originalShareCount >= 0 &&
          resultingShareCount > originalShareCount
        ) {
          resultingCostBasis =
            (originalShareCount * originalCostBasis +
              adjustedShare * adjustedPrice) /
            (originalShareCount + adjustedShare);
        } else if (
          originalShareCount > 0 &&
          resultingShareCount >= 0 &&
          resultingShareCount < originalShareCount
        ) {
          resultingCostBasis = originalCostBasis;
        } else if (originalShareCount > 0 && resultingShareCount < 0) {
          resultingCostBasis = adjustedPrice;
        } else if (
          originalShareCount <= 0 &&
          resultingShareCount < originalShareCount
        ) {
          resultingCostBasis =
            (originalShareCount * originalCostBasis +
              adjustedShare * adjustedPrice) /
            (originalShareCount + adjustedShare);
        } else if (
          originalShareCount < 0 &&
          resultingShareCount <= 0 &&
          resultingShareCount > originalShareCount
        ) {
          resultingCostBasis = originalCostBasis;
        } else if (originalShareCount < 0 && resultingShareCount > 0) {
          resultingCostBasis = adjustedPrice;
        }
        if (resultingShareCount === 0) {
          resultingCostBasis = 0;
        }

        let pnl = 0;
        if (
          (originalShareCount >= 0 &&
            resultingShareCount >= 0 &&
            resultingShareCount < originalShareCount) ||
          (originalShareCount <= 0 &&
            resultingShareCount <= 0 &&
            resultingShareCount > originalShareCount)
        ) {
          pnl =
            (originalShareCount - resultingShareCount) *
            (adjustedPrice - originalCostBasis);
        } else if (
          (originalShareCount > 0 && resultingShareCount < 0) ||
          (originalShareCount < 0 && resultingShareCount > 0)
        ) {
          pnl = originalShareCount * (adjustedPrice - originalCostBasis);
        }

        const proceeds = adjustedPrice * adjustedShare;

        const cumulativePnL = tempArray.reduce((acc, each) => {
          acc += each.pnl;
          return acc;
        }, pnl);

        tempArray.push({
          pnl,
          cumulativePnL,
          originalCostBasis,
          resultingCostBasis,
          originalShareCount,
          resultingShareCount,
          proceeds,
          effectiveAdjustMultiple,
          adjustedShare,
          currentHolding,
          adjustedPrice,
          unix: tradeDateUnix,
          date: trade.date,
          account: trade.account,
          ticker: trade.ticker,
          share: trade.share,
          price: trade.price,
          currency: trade.currency,
          commission: trade.commission,
          cumulativePnLIndex: idx,
          _id: trade._id,
        });
      });
      return tempArray;
    };

    const returnArrayObj: DisplayDataParsed = {
      accounts: {},
      realizedPnL: 0,
      unrealizedPnL: 0,
      overallPnL: 0,
      currentHoldings: {},
    };

    const global = {
      realizedPnL: 0,
      unrealizedPnL: 0,
      currentHoldings: {} as Record<string, number>,
    };

    const allTradesByTicker: Record<
      string,
      Array<{
        trades: ParsedTrade[];
        realizedPnL: number;
        unrealizedPnL: number;
        resultingShareCount: number;
        costBasis: number;
      }>
    > = {};

    for (const accountKey of Object.keys(tradesByAccount)) {
      const tradesByTicker: Record<string, Trade[]> = {};
      for (const trade of tradesByAccount?.[accountKey] || []) {
        if (!tradesByTicker[trade.ticker]) tradesByTicker[trade.ticker] = [];
        tradesByTicker[trade.ticker].push(trade);
      }

      const account = {
        trades: {} as Record<
          string,
          {
            trades: ParsedTrade[];
            realizedPnL: number;
            unrealizedPnL: number;
            overallPnL: number;
            resultingShareCount: number;
            longShort?: "Long" | "Short";
            gainLoss?: "gain" | "loss";
          }
        >,
        realizedPnL: 0,
        unrealizedPnL: 0,
        overallPnL: 0,
        currentHoldings: {} as Record<string, number>,
      };

      for (const [ticker] of Object.entries(tradesByTicker)) {
        const trades = parseTicker(ticker, tradesByTicker);
        const lastTrade = trades[trades.length - 1] || {};
        const { resultingShareCount = 0, resultingCostBasis = 0 } = lastTrade;

        const realizedPnL = trades.reduce((acc, t) => acc + t.pnl, 0);
        const unrealizedPnL =
          resultingShareCount *
            ((unrealizedTickersPrice[ticker] || resultingCostBasis) -
              resultingCostBasis) || 0;

        const overallPnL = realizedPnL + unrealizedPnL;

        account.trades[ticker] = {
          trades,
          realizedPnL,
          unrealizedPnL,
          overallPnL,
          resultingShareCount,
          longShort: resultingShareCount > 0 ? "Long" : "Short",
          gainLoss: overallPnL > 0 ? "gain" : "loss",
        };

        if (Math.abs(resultingShareCount) > 0.000001) {
          account.currentHoldings[ticker] = resultingShareCount;
        }

        account.realizedPnL += realizedPnL;
        account.unrealizedPnL += unrealizedPnL;

        if (!allTradesByTicker[ticker]) {
          allTradesByTicker[ticker] = [];
        }
        allTradesByTicker[ticker].push({
          trades,
          realizedPnL,
          unrealizedPnL,
          resultingShareCount,
          costBasis: resultingCostBasis,
        });
      }

      account.overallPnL = account.realizedPnL + account.unrealizedPnL;
      returnArrayObj.accounts[accountKey] = account;

      global.realizedPnL += account.realizedPnL;
      global.unrealizedPnL += account.unrealizedPnL;

      for (const [ticker, count] of Object.entries(account.currentHoldings)) {
        global.currentHoldings[ticker] =
          (global.currentHoldings[ticker] || 0) + count;
      }
    }

    returnArrayObj.realizedPnL = global.realizedPnL;
    returnArrayObj.unrealizedPnL = global.unrealizedPnL;
    returnArrayObj.overallPnL = global.realizedPnL + global.unrealizedPnL;
    returnArrayObj.currentHoldings = global.currentHoldings;

    const allAccount = {
      realizedPnL: global.realizedPnL,
      unrealizedPnL: global.unrealizedPnL,
      overallPnL: global.realizedPnL + global.unrealizedPnL,
      currentHoldings: global.currentHoldings,
      trades: {} as Record<
        string,
        {
          trades: ParsedTrade[];
          realizedPnL: number;
          unrealizedPnL: number;
          overallPnL: number;
          resultingShareCount: number;
        }
      >,
    };

    for (const [ticker, tradeSets] of Object.entries(allTradesByTicker)) {
      let combinedTrades: ParsedTrade[] = [];
      let totalRealized = 0;
      let totalUnrealized = 0;
      let totalShares = 0;

      for (const t of tradeSets) {
        combinedTrades = combinedTrades.concat(t.trades);
        totalRealized += t.realizedPnL;
        totalUnrealized += t.unrealizedPnL;
        totalShares += t.resultingShareCount;
      }
      allAccount.trades[ticker] = {
        trades: combinedTrades,
        realizedPnL: totalRealized,
        unrealizedPnL: totalUnrealized,
        overallPnL: totalRealized + totalUnrealized,
        resultingShareCount: totalShares,
      };
    }

    returnArrayObj.accounts[allAccountString] = allAccount;

    return returnArrayObj;
  }, [
    tradesByAccount,
    splitAdjustHistory,
    unrealizedTickersPrice,
    allAccountString,
  ]);

  const currencyTicker: CurrencyTicker = useMemo(() => {
    return { CAD: ["USDCAD=X", "CAD=X"] };
  }, []);

  const conversionModes: ConversionModes = useMemo(() => {
    return {
      USD: { label: "USD", rate: 1 },
      CAD: {
        label: "CAD",
        rate:
          unrealizedTickersPrice[currencyTicker?.CAD[0]] ||
          unrealizedTickersPrice[currencyTicker?.CAD[1]] ||
          1,
      },
    };
  }, [unrealizedTickersPrice, currencyTicker]);

  const [searchThisTicker, setSearchThisTicker] = useState<string>("");

  const holdingDayChange: number = useMemo(() => {
    const targetHoldingObj =
      selectedAccount === allAccountString
        ? displayDataParsed?.currentHoldings
        : displayDataParsed?.accounts?.[selectedAccount]?.currentHoldings;
    return Object.keys(targetHoldingObj || {}).reduce((acc, eachTickerKey) => {
      const shares = targetHoldingObj?.[eachTickerKey] || 0;
      const close = prevClosePrice[eachTickerKey];
      const last = lastPrice[eachTickerKey];
      acc += (last - close) * shares || 0;
      return acc;
    }, 0);
  }, [
    displayDataParsed,
    lastPrice,
    prevClosePrice,
    selectedAccount,
    allAccountString,
  ]);

  const holdingMarketValue: number = useMemo(() => {
    const targetHoldingObj =
      selectedAccount === allAccountString
        ? displayDataParsed?.currentHoldings
        : displayDataParsed?.accounts?.[selectedAccount]?.currentHoldings;
    return Object.keys(targetHoldingObj || {}).reduce((acc, eachTickerKey) => {
      const shares = targetHoldingObj?.[eachTickerKey] || 0;
      const last = lastPrice[eachTickerKey];
      acc += last * shares || 0;
      return acc;
    }, 0);
  }, [displayDataParsed, lastPrice, selectedAccount, allAccountString]);

  const unrealizedTickers: string[] = useMemo(() => {
    const tempArray = [
      currencyTicker?.CAD[0],
      ...(displayDataParsed.currentHoldings
        ? Object.keys(displayDataParsed.currentHoldings)
        : []),
    ];
    return tempArray;
  }, [displayDataParsed, currencyTicker]);

  useEffect(() => {
    if (hasAutoSelectedTicker.current) return;
    const holdings = displayDataParsed?.currentHoldings;
    if (!holdings || Object.keys(holdings).length === 0) return;
    if (Object.keys(lastPrice).length === 0) return;
    const allPricesLoaded = Object.keys(holdings).every(
      (ticker) => lastPrice[ticker] !== undefined,
    );
    if (!allPricesLoaded) return;

    let bestTicker = "";
    let bestValue = 0;
    for (const ticker of Object.keys(holdings)) {
      const mv = Math.abs((holdings[ticker] || 0) * (lastPrice[ticker] || 0));
      if (mv > bestValue) {
        bestValue = mv;
        bestTicker = ticker;
      }
    }
    if (bestTicker) {
      setSelectedTicker(bestTicker);
      hasAutoSelectedTicker.current = true;
    }
  }, [displayDataParsed?.currentHoldings, lastPrice]);

  const lastClosePriceDelta: number = useMemo(() => {
    return lastPrice[selectedTicker] - prevClosePrice[selectedTicker] || 0;
  }, [lastPrice, selectedTicker, prevClosePrice]);

  const lastClosePriceCompare: "neutral" | "negative" | "positive" =
    useMemo(() => {
      return lastClosePriceDelta === 0 || isNaN(lastClosePriceDelta)
        ? "neutral"
        : lastClosePriceDelta < 0
          ? "negative"
          : "positive";
    }, [lastClosePriceDelta]);

  const currencyMultiplier: number = useMemo(() => {
    return (
      conversionModes[selectedCurrency as keyof ConversionModes]?.rate || 1
    );
  }, [conversionModes, selectedCurrency]);

  const tradesDataArray: ParsedTrade[] = useMemo(() => {
    if (!displayDataParsed?.accounts) return [];
    let returnArray: ParsedTrade[] = [];
    if (selectedAccount === allAccountString) {
      if (selectedTicker === allTickerString) {
        Object.keys(displayDataParsed?.accounts).forEach((eachAccountKey) => {
          if (eachAccountKey !== allAccountString) {
            Object.keys(
              displayDataParsed?.accounts?.[eachAccountKey]?.trades || {},
            )?.forEach((eachTradeKey) => {
              displayDataParsed?.accounts?.[eachAccountKey]?.trades?.[
                eachTradeKey
              ]?.trades?.forEach((eachTrade: ParsedTrade) => {
                returnArray.push(eachTrade);
              });
            });
          }
        });
      } else {
        Object.keys(displayDataParsed?.accounts).forEach((eachAccountKey) => {
          if (eachAccountKey !== allAccountString) {
            Object.keys(
              displayDataParsed?.accounts?.[eachAccountKey]?.trades || {},
            )?.forEach((eachTradeKey) => {
              displayDataParsed?.accounts?.[eachAccountKey]?.trades?.[
                eachTradeKey
              ]?.trades?.forEach((eachTrade: ParsedTrade) => {
                if (eachTrade.ticker === selectedTicker) {
                  returnArray.push(eachTrade);
                }
              });
            });
          }
        });
      }
    } else {
      if (selectedTicker === allTickerString) {
        Object.keys(
          displayDataParsed?.accounts?.[selectedAccount]?.trades || {},
        ).forEach((eachTickerKey) => {
          returnArray = [
            ...returnArray,
            ...(displayDataParsed?.accounts?.[selectedAccount]?.trades?.[
              eachTickerKey
            ]?.trades || []),
          ];
        });
      } else {
        returnArray =
          displayDataParsed?.accounts?.[selectedAccount]?.trades?.[
            selectedTicker
          ]?.trades || [];
      }
    }

    return returnArray?.sort((a, b) => {
      if (b.unix - a.unix) {
        return b.unix - a.unix;
      }
      return b.cumulativePnLIndex - a.cumulativePnLIndex;
    });
  }, [
    selectedAccount,
    displayDataParsed,
    selectedTicker,
    allAccountString,
    allTickerString,
  ]);

  const chartDataSelected: ChartDataResult | Record<string, never> =
    useMemo(() => {
      const key = `${selectedTicker}_${chartParams.interval}_${chartParams.range}`;
      const returnObj = chartData[key] || {};
      const quoteObj = returnObj?.indicators?.quote?.[0];
      const timestamps = returnObj?.timestamp || [];

      const secondLastIdx = timestamps.length - 2;

      const shouldFilterSecond =
        quoteObj &&
        ["open", "high", "low", "close", "volume"].every(
          (field) =>
            quoteObj[field as keyof typeof quoteObj]?.[secondLastIdx] === null,
        );

      const filterValues = <T,>(arr: T[] | undefined): T[] | undefined =>
        shouldFilterSecond
          ? arr?.filter((_, idx) => idx !== secondLastIdx)
          : arr;

      if (quoteObj && timestamps.length) {
        const filteredQuote: Record<string, (number | null)[] | undefined> = {};
        (["open", "high", "low", "close", "volume"] as const).forEach(
          (field) => {
            filteredQuote[field] = filterValues(quoteObj[field]);
          },
        );

        return {
          ...returnObj,
          timestamp: filterValues(timestamps) || [],
          indicators: {
            quote: [filteredQuote as ChartDataResult["indicators"]["quote"][0]],
          },
        };
      }
      return returnObj;
    }, [selectedTicker, chartParams, chartData]);

  const zoomFilteredChartData: ChartDataResult | Record<string, never> =
    useMemo(() => {
      if (
        !zoomPortionIndex ||
        isNaN(zoomPortionIndex.min) ||
        isNaN(zoomPortionIndex.max)
      ) {
        return chartDataSelected;
      }

      const chartDataSelectedCopy = cloneDeep(
        chartDataSelected,
      ) as ChartDataResult;

      const trimZoom = <T,>(array: T[] | undefined): T[] | undefined => {
        if (!array) return array;
        return array.filter(
          (_, idx) =>
            zoomPortionIndex.min <= idx && zoomPortionIndex.max >= idx,
        );
      };

      if (chartDataSelectedCopy.timestamp) {
        chartDataSelectedCopy.timestamp =
          trimZoom(chartDataSelectedCopy.timestamp) || [];
      }

      const { indicators } = chartDataSelectedCopy;

      const adjcloseFields = indicators?.adjclose?.[0];
      if (adjcloseFields) {
        adjcloseFields.adjclose = trimZoom(adjcloseFields.adjclose) || [];
      }

      const quoteFields = indicators?.quote?.[0];
      if (quoteFields) {
        Object.entries(quoteFields).forEach(([key, value]) => {
          if (Array.isArray(value)) {
            (quoteFields as Record<string, (number | null)[]>)[key] =
              trimZoom(value) || [];
          }
        });
      }

      return chartDataSelectedCopy;
    }, [zoomPortionIndex, chartDataSelected]);

  const selectedTickerCurrency: string = useMemo(() => {
    return (
      chartData[
        `${selectedTicker}_${chartParams?.interval}_${chartParams?.range}`
      ]?.meta?.currency ||
      tradesDataArray.find((each) => each.ticker === selectedTicker)
        ?.currency ||
      "CUR"
    );
  }, [chartData, chartParams, selectedTicker, tradesDataArray]);

  const findFilteredIndex: { minIndex: number; maxIndex: number } =
    useMemo(() => {
      let minIndex = 0;
      let maxIndex =
        (chartDataSelected as ChartDataResult)?.timestamp?.length - 1 || 0;
      (chartDataSelected as ChartDataResult)?.timestamp?.forEach(
        (timestamp: number, index: number) => {
          if (timestamp < cutOffDateChart.min) {
            minIndex = index;
          }
          if (timestamp < cutOffDateChart.max + 43200) {
            maxIndex = index;
          }
        },
      );
      return { minIndex, maxIndex };
    }, [chartDataSelected, cutOffDateChart]);

  const [bulkText, setBulkText] = useState<string>("");
  const [uploadArray, setUploadArray] = useState<Trade[]>([]);
  const [uploadArrayError, setUploadArrayError] = useState<string>("");
  const [replaceMode, setReplaceMode] = useState<boolean>(false);

  const tradeTemplate: TradeTemplate = {
    ticker: "",
    price: "",
    share: "",
    account: "",
    date: "",
    commission: "",
    currency: "USD",
  };

  const [fieldObj, setFieldObj] = useState<TradeTemplate>(tradeTemplate);

  const pinCodeLife = 24 * hourToMinutes * minuteToSeconds * seconds;
  const pinCodeSetTime = useRef<number>(Date.now());
  const [pinCode, setPinCode] = useState<string>("999");
  const [privateMode, setPrivateMode] = useState<boolean>(false);

  useEffect(() => {
    setPrivateMode([privacyPassword, deletePassword].includes(pinCode));
  }, [pinCode]);

  const [adminMode, setAdminMode] = useState<boolean>(true);

  useEffect(() => {
    setAdminMode([deletePassword].includes(pinCode));
  }, [pinCode]);

  const isMarketTime: boolean = useMemo(() => {
    const timeNow = Date.now() / 1000;
    return (
      (marketTimes[`pre-market`].open < timeNow &&
        marketTimes[`pre-market`].close > timeNow) ||
      (marketTimes[`market`].open < timeNow &&
        marketTimes[`market`].close > timeNow) ||
      (marketTimes[`after-market`].open < timeNow &&
        marketTimes[`after-market`].close > timeNow)
    );
  }, [marketTimes]);

  const marketStatus: MarketStatus = useMemo(() => {
    const timeNow = Date.now() / 1000;
    let title: MarketStatus["title"] = "CLOSED";
    let color = "rgb(0,0,0)";

    if (
      marketTimes[`pre-market`].open < timeNow &&
      marketTimes[`pre-market`].close > timeNow
    ) {
      title = "PRE-MARKET";
      color = "orange";
    } else if (
      marketTimes[`market`].open < timeNow &&
      marketTimes[`market`].close > timeNow
    ) {
      title = "MARKET-HOUR";
      color = positiveGreen;
    } else if (
      marketTimes[`after-market`].open < timeNow &&
      marketTimes[`after-market`].close > timeNow
    ) {
      title = "AFTER-MARKET";
      color = "orange";
    } else {
      title = "CLOSED";
      color = "rgb(100,100,100)";
    }
    return { title, color };
  }, [marketTimes]);

  const [ignoreTokenLimit, setIgnoreTokenLimit] = useState<boolean>(false);

  useEffect(() => {
    try {
      const json = JSON.parse(bulkText);
      if (Array.isArray(json)) {
        setUploadArray(json);
        setUploadArrayError("");
      } else {
        setUploadArrayError("Input is not an array.");
      }
    } catch (err) {
      runServiceHealthCheck();
      setUploadArrayError("Invalid JSON: " + (err as Error).message);
    }
  }, [bulkText, runServiceHealthCheck]);

  const numFormat = useCallback(
    (
      num: number,
      sensitive: boolean | null = null,
      decimals: number = 2,
    ): string => {
      const starString = "********";
      if (!isLogged && sensitive) return starString;
      if (isNaN(Number(num))) {
        if (!privateMode && !adminMode && sensitive) {
          return starString;
        } else {
          return "0.00";
        }
      }
      const temp = (Math.abs(num) < 0.000000001 ? 0 : num)?.toLocaleString(
        "en-US",
        {
          minimumFractionDigits: decimals,
          maximumFractionDigits: decimals,
        },
      );
      if (!privateMode && !adminMode && sensitive) {
        return starString;
      } else {
        return temp;
      }
    },
    [privateMode, adminMode, isLogged],
  );

  useEffect(() => {
    let title = `TradeSapien`;
    let addOn = "";
    if (
      selectedTicker !== allTickerString &&
      lastPrice[selectedTicker] &&
      prevClosePrice[selectedTicker]
    ) {
      addOn = ` [${selectedTicker}: ${
        lastPrice[selectedTicker] - prevClosePrice[selectedTicker] > 0
          ? "+"
          : ""
      }${numFormat(
        (100 * (lastPrice[selectedTicker] - prevClosePrice[selectedTicker])) /
          prevClosePrice[selectedTicker],
        false,
        2,
      )}%]`;
    }
    document.title = title + addOn;
  }, [
    chartData,
    selectedTicker,
    lastPrice,
    numFormat,
    prevClosePrice,
    allTickerString,
  ]);

  const getMaxRangeForInterval = useCallback(
    (interval: IntervalOption): string | null => {
      const ranges = Object.entries(rangeOption2);
      let lastMatch: string | null = null;

      for (const [range, intervals] of ranges) {
        if ((intervals as IntervalOption[]).includes(interval)) {
          lastMatch = range;
        }
      }
      return lastMatch;
    },
    [rangeOption2],
  );

  const handleChangeParam = (
    type: "range" | "interval",
    option: string,
  ): void => {
    const next = { ...chartParams };

    if (type === "range") {
      next.range = option as ChartParams["range"];
      const newInterval =
        getMostGranularInterval[option as keyof MostGranularIntervalMap] ||
        null;

      if (
        newInterval &&
        newInterval !== chartParams.interval &&
        !rangeOption2[option]?.includes(chartParams.interval)
      ) {
        next.interval = newInterval;
      }
    }

    if (type === "interval") {
      next.interval = option as ChartParams["interval"];

      if (
        !rangeOption2[chartParams.range]?.includes(option as IntervalOption)
      ) {
        const newRange = getMaxRangeForInterval(option as IntervalOption);
        if (newRange && newRange !== chartParams.range) {
          next.range = newRange as ChartParams["range"];
        }
      }
    }

    if (
      next.range !== chartParams.range ||
      next.interval !== chartParams.interval
    ) {
      setChartParams(next);
    }
  };

  const handleSearchTicker = (): void => {
    if (searchTickerChart !== "") {
      setSearchThisTicker(searchTickerChart.toUpperCase());
      setSelectedTicker(searchTickerChart.toUpperCase());
    }
  };

  const handlePinCode = (e: React.ChangeEvent<HTMLInputElement>): void => {
    const input = e.target.value;
    const prevLength = pinCode.length;
    const newLength = input.length;

    if (newLength > prevLength) {
      const newChar = input[newLength - 1];
      setPinCode(pinCode + newChar);
    } else {
      setPinCode(pinCode.slice(0, newLength));
    }
  };

  useEffect(() => {
    if (privateMode || adminMode) {
      pinCodeSetTime.current = Date.now();
    }
  }, [privateMode, adminMode, resetIdleTrigger]);

  useEffect(() => {
    if (
      (privateMode || adminMode) &&
      Date.now() - pinCodeSetTime.current >= pinCodeLife
    ) {
      setPinCode("");
      pinCodeSetTime.current = Date.now();
    }
  }, [autoRefreshClock, privateMode, adminMode, pinCodeLife, setPinCode]);

  const recordUpToDateTickerInfo = useCallback(
    (
      ticker: string,
      data: { chart?: { result?: ChartDataResult[] } },
    ): void => {
      const obj = data?.chart?.result?.[0];
      if (!obj) return;

      const closeArray = obj?.indicators?.quote?.[0]?.close || [];
      let lastPriceValue: number | null = null;

      if (marketStatus.title === "PRE-MARKET") {
        lastPriceValue = closeArray[closeArray.length - 1];
      } else if (marketStatus.title === "MARKET-HOUR") {
        lastPriceValue = closeArray[closeArray.length - 1];
      } else if (marketStatus.title === "AFTER-MARKET") {
        lastPriceValue = closeArray[closeArray.length - 1];
      } else if (marketStatus.title === "CLOSED") {
        lastPriceValue = closeArray[closeArray.length - 1];
      }

      setLastPriceActual((prev) => ({
        ...prev,
        [`${ticker}`]: testPrices[ticker]
          ? testPrices[ticker]
          : lastPriceValue || 0,
      }));

      let prevClose: number | undefined;
      if (marketStatus.title === "PRE-MARKET") {
        prevClose = obj?.meta?.regularMarketPrice;
      } else if (marketStatus.title === "MARKET-HOUR") {
        prevClose = obj?.meta?.previousClose;
      } else if (marketStatus.title === "AFTER-MARKET") {
        prevClose = obj?.meta?.previousClose;
      } else if (marketStatus.title === "CLOSED") {
        prevClose = obj?.meta?.previousClose;
      }

      setPrevClosePrice((prev) => ({
        ...prev,
        [`${ticker}`]: prevClose || 0,
      }));
      setLongName((prev) => ({
        ...prev,
        [`${ticker}`]: obj?.meta?.longName || "",
      }));
      setVolume((prev) => ({
        ...prev,
        [`${ticker}`]: obj?.meta?.regularMarketVolume || 0,
      }));

      let marketClosePrice = obj?.meta?.regularMarketPrice;
      if (marketStatus.title === "PRE-MARKET") {
        marketClosePrice = obj?.meta?.regularMarketPrice;
      } else if (marketStatus.title === "MARKET-HOUR") {
        marketClosePrice = undefined;
      } else if (marketStatus.title === "AFTER-MARKET") {
        marketClosePrice = obj?.meta?.regularMarketPrice;
      } else if (marketStatus.title === "CLOSED") {
        marketClosePrice = obj?.meta?.regularMarketPrice;
      }

      setMarketClosingPrice((prev) => ({
        ...prev,
        [`${ticker}`]: marketClosePrice || 0,
      }));
    },
    [testPrices, marketStatus],
  );

  const addDataToState = useCallback(
    (
      data: { chart?: { result?: ChartDataResult[] } },
      queryTicker: string,
      queryInterval: string,
      queryRange: string,
    ): void => {
      const result = data?.chart?.result?.[0];
      if (data && result) {
        setChartData((prev) => ({
          ...prev,
          [`${queryTicker}_${queryInterval}_${queryRange}`]: result,
        }));
      }
    },
    [],
  );

  const chartCurrentParams = useRef<string | null>(null);

  const resetZoomIndex = useCallback((): void => {
    const acceptableRefreshZone = 1;
    const timestampsLength =
      (chartDataSelected as ChartDataResult)?.timestamp?.length ?? 0;

    if (timestampsLength === 0) return;

    const currentKey = `${selectedTicker}_${chartParams?.interval}_${chartParams?.range}`;
    const current = chartCurrentParams.current;

    if (
      current === currentKey &&
      zoomPortionIndex.max + 1 >= timestampsLength - 1 - acceptableRefreshZone
    ) {
      setZoomPortionIndex((prev) => {
        const originalLength = prev.max - prev.min;

        return {
          min: prev.min === 0 ? 0 : timestampsLength - 1 - originalLength,
          max: timestampsLength - 1,
        };
      });
      chartCurrentParams.current = currentKey;
    } else if (!current || current !== currentKey) {
      setZoomPortionIndex({
        min: 0,
        max: timestampsLength - 1,
      });
      chartCurrentParams.current = currentKey;
    }
  }, [
    selectedTicker,
    chartParams?.interval,
    chartParams?.range,
    chartDataSelected,
    zoomPortionIndex.max,
  ]);

  const getHoldingInfo = (account: string, ticker: string): HoldingInfo => {
    let shareCount = 0;
    let totalCost = 0;
    let totalPnL = 0;
    let costBasis: number | null = null;

    const filteredTrades =
      account === allAccountString
        ? tradesDataArray
        : tradesDataArray?.filter(
            (trade) => trade.account === account && trade.ticker === ticker,
          );

    const uniqueAccounts = new Set<string>();

    filteredTrades?.forEach((trade) => {
      if (!uniqueAccounts.has(trade.account)) {
        shareCount += trade.resultingShareCount;
        totalCost += trade.resultingShareCount * trade.resultingCostBasis;
        totalPnL += trade.cumulativePnL;
        uniqueAccounts.add(trade.account);
      }
    });

    if (account !== allAccountString) {
      const latestTrade = filteredTrades?.sort((a, b) => b.unix - a.unix)[0];
      costBasis = latestTrade?.resultingCostBasis ?? null;
    } else {
      costBasis = shareCount > 0 ? totalCost / shareCount : null;
    }

    return {
      costBasis,
      costBasisWithRealized:
        shareCount > 0 ? (totalCost - totalPnL) / shareCount : null,
    };
  };

  const getSplitMultiplier = (ticker: string, timestamp: number): number => {
    if (
      !splitAdjustHistory?.[ticker] ||
      Object.keys(splitAdjustHistory?.[ticker]).length === 0
    )
      return 1;
    return Object.keys(splitAdjustHistory[ticker])?.reduce(
      (acc, eachSplitObjKey) => {
        if (
          splitAdjustHistory[ticker][eachSplitObjKey].date > timestamp &&
          splitAdjustHistory[ticker][eachSplitObjKey].numerator &&
          splitAdjustHistory[ticker][eachSplitObjKey].denominator
        ) {
          acc *=
            splitAdjustHistory[ticker][eachSplitObjKey].numerator /
            splitAdjustHistory[ticker][eachSplitObjKey].denominator;
        }
        return acc;
      },
      1,
    );
  };

  const chartCall = useCallback(
    async (
      ticker: string,
      interval: string,
      range: string,
    ): Promise<AxiosResponse<StockDataResponse> | undefined> => {
      let result: AxiosResponse<StockDataResponse> | undefined;
      try {
        result = await axios.post<StockDataResponse>(`/stockData/getData`, {
          ticker,
          interval,
          range,
        });
      } catch (err) {
        runServiceHealthCheck();
      }
      return result;
    },
    [runServiceHealthCheck],
  );

  const handleNewList = (tickerArray: string[]): void => {
    tickerListPosted.current = tickerArray;
    tickerListCalled.current.length = 0;
  };

  const marketRefreshReady = useRef<boolean>(false);

  const runDirect = useCallback(async () => {
    if (directIsCalled.current || isRunning) return;
    if (!serverActive.current) return;
    // Ensure currency data loads before other tickers
    if (selectedTicker !== currencyTicker?.CAD[0]) {
      const currencyKey = `${currencyTicker?.CAD[0]}_${defaultInfoInterval}_${defaultInfoRange}`;
      if (!chartData[currencyKey]) return;
    }
    const chartKey = `${selectedTicker}_${chartParams?.interval}_${chartParams?.range}`;
    const hasChartData = !!chartData[chartKey];
    if (!marketRefreshReady.current && hasChartData) return;
    if (!hasChartData) {
      try {
        setIsRunning(true);
        directIsCalled.current = true;
        let result: AxiosResponse<StockDataResponse> | undefined = undefined;
        let resultAlt: AxiosResponse<StockDataResponse> | undefined = undefined;

        result = await chartCall(
          selectedTicker,
          chartParams?.interval,
          chartParams?.range,
        );

        if (
          !["1m", "2m", "5m", "15m", "30m", "60m", "90m"].includes(
            chartParams?.interval,
          )
        ) {
          resultAlt = await chartCall(selectedTicker, "1m", "1d");
        }

        if (result) {
          addDataToState(
            result.data,
            selectedTicker,
            chartParams?.interval,
            chartParams?.range,
          );
        }
        const targetResult = resultAlt || result;
        if (targetResult) {
          recordUpToDateTickerInfo(selectedTicker, targetResult.data);
        }
      } catch (err) {
        runServiceHealthCheck();
        console.error("Error fetching stock price:", (err as Error).message);
      } finally {
        directIsCalled.current = false;
        setIsRunning(false);
      }
    }
  }, [
    selectedTicker,
    chartParams,
    chartData,
    addDataToState,
    recordUpToDateTickerInfo,
    isRunning,
    chartCall,
    runServiceHealthCheck,
    currencyTicker,
  ]);

  useEffect(() => {
    if (serverActive.current && autoRefreshClock !== 0) {
      runDirect();
    }
  }, [selectedTicker, runDirect, autoRefreshClock]);

  const handleGetChartData = useCallback(
    async (ticker: string, interval: string, range: string): Promise<void> => {
      if (sequenceIsCalled.current) return;
      if (!serverActive.current) {
        runServiceHealthCheck();
        return;
      }
      try {
        sequenceIsCalled.current = true;
        let result: AxiosResponse<StockDataResponse> | undefined = undefined;
        let resultAlt: AxiosResponse<StockDataResponse> | undefined = undefined;

        if (ticker === currencyTicker?.CAD[0]) {
          result = await chartCall(ticker, "1m", "1d");
        } else {
          result = await chartCall(ticker, interval, range);

          if (
            !["1m", "2m", "5m", "15m", "30m", "60m", "90m"].includes(interval)
          ) {
            resultAlt = await chartCall(
              ticker,
              defaultInfoInterval,
              defaultInfoRange,
            );
          }
        }
        if (result) {
          addDataToState(result.data, ticker, interval, range);
        }
        const targetResult = resultAlt || result;
        if (targetResult) {
          recordUpToDateTickerInfo(ticker, targetResult.data);
        }
      } catch (err) {
        runServiceHealthCheck();
        console.error("Error fetching stock price:", (err as Error).message);
      } finally {
        sequenceIsCalled.current = false;
        tickerListCalled?.current?.push(ticker);
        lastUpdateTime.current = Math.floor(Date.now() / 1000);
      }
    },
    [
      addDataToState,
      recordUpToDateTickerInfo,
      currencyTicker,
      chartCall,
      runServiceHealthCheck,
    ],
  );

  const runSequential = useCallback(
    async (tickerArray: string[]) => {
      if (!serverActive.current) return;
      for (let i = 0; i < tickerArray.length; i++) {
        if (!tickerListCalled?.current?.includes(tickerArray[i])) {
          if (selectedTicker === tickerArray[i]) {
            handleGetChartData(
              tickerArray[i],
              chartParams?.interval,
              chartParams?.range,
            );
          } else {
            handleGetChartData(
              tickerArray[i],
              defaultInfoInterval,
              defaultInfoRange,
            );
          }
          break;
        }
      }
    },
    [handleGetChartData, selectedTicker, chartParams],
  );

  useEffect(() => {
    let same = true;
    const sorted1 = tickerListPosted?.current.sort();
    const sorted2 = [
      ...new Set([
        ...unrealizedTickers,
        selectedTicker,
        ...(isLogged ? user?.watchlist : defaultWatchList),
      ]),
    ].sort();
    if (sorted1.length !== sorted2.length) {
      same = false;
    }
    for (let i = 0; i < sorted1.length; i++) {
      if (sorted1[i] !== sorted2[i]) {
        same = false;
        break;
      }
    }
    if (!same) {
      handleNewList([
        ...new Set([
          ...unrealizedTickers,
          selectedTicker,
          ...(isLogged ? user?.watchlist : defaultWatchList),
        ]),
      ]);
    }
  }, [user, unrealizedTickers, selectedTicker, isLogged, defaultWatchList]);

  useEffect(() => {
    marketRefreshReady.current =
      (isMarketTime && refreshToken > 0) || ignoreTokenLimit;
  }, [refreshToken, ignoreTokenLimit, isMarketTime]);

  useEffect(() => {
    if (!serverActive.current) return;
    let fullfilled = true;
    const sorted1 = tickerListPosted?.current.sort();
    const sorted2 = tickerListCalled?.current.sort();
    for (let i = 0; i < sorted1.length; i++) {
      if (sorted1[i] !== sorted2[i]) {
        fullfilled = false;
        break;
      }
    }
    if (fullfilled) {
      setIsRunning(false);
      const prevRefreshBucket = Math.floor(
        prevAutoRefreshClock.current / sequenceRefreshRate,
      );
      const currRefreshBucket = Math.floor(
        autoRefreshClock / sequenceRefreshRate,
      );
      if (currRefreshBucket > prevRefreshBucket) {
        if (!marketRefreshReady.current) return;
        const missedIntervals = currRefreshBucket - prevRefreshBucket;
        if (
          missedIntervals > 1 ||
          currRefreshBucket % chartToBackgroundRefreshRatio === 0
        ) {
          tickerListCalled.current.length = 0;
        } else {
          tickerListCalled.current = tickerListCalled.current.filter(
            (each) => each !== selectedTicker,
          );
        }
        setRefreshToken((prev) => Math.max(0, prev - missedIntervals));
      }
    } else if (autoRefreshClock > 0) {
      setIsRunning(true);
      runSequential(tickerListPosted.current);
    }
  }, [
    autoRefreshClock,
    chartData,
    runSequential,
    selectedTicker,
    ignoreTokenLimit,
  ]);

  // Track previous clock value for boundary-crossing checks (must be last autoRefreshClock effect)
  useEffect(() => {
    prevAutoRefreshClock.current = autoRefreshClock;
  }, [autoRefreshClock]);

  return (
    <AppContext.Provider
      value={{
        navigate,
        location,
        user,
        setUser,
        isLogged,
        handleLogout,
        showLoginModal,
        setShowLoginModal,
        autoRefreshClock,
        setAutoRefreshClock,
        sequenceRefreshRate,
        seconds,
        minuteToSeconds,
        hourToMinutes,
        maxStartingRefreshToken,
        chartToBackgroundRefreshRatio,
        useApiFetch,
        defaultWatchList,
        tickerListCalled,
        sequenceIsCalled,
        pinInput,
        lastUpdateTime,
        sequentialRunning,
        getFont,
        privacyPassword,
        deletePassword,
        allTickerString,
        allAccountString,
        refreshToken,
        setRefreshToken,
        resetIdleTrigger,
        setResetIdleTrigger,
        blink,
        setBlink,
        chartParams,
        setChartParams,
        showCandleChart,
        setShowCandleChart,
        showVolume,
        setShowVolume,
        showWick,
        setShowWick,
        showTrades,
        setShowTrades,
        searchTickerChart,
        setSearchTickerChart,
        chartData,
        setChartData,
        rangeOption2,
        getMostGranularInterval,
        windowSecondsByInterval,
        intervalOption,
        darkMode,
        setDarkMode,
        isDraggingChart,
        setIsDraggingChart,
        selectedTicker,
        setSelectedTicker,
        selectedAccount,
        setSelectedAccount,
        selectedCurrency,
        setSelectedCurrency,
        accountInfo,
        setAccountInfo,
        tradesByAccount,
        setTradesByAccount,
        tickerQueue,
        setTickerQueue,
        expandChart,
        setExpandChart,
        expandWatchlist,
        setExpandWatchlist,
        splitAdjustHistory,
        setSplitAdjustHistory,
        selectedTradePointDetail,
        setSelectedTradePointDetail,
        badTickerCallCount,
        setBadTickerCallCount,
        zoomPortionIndex,
        setZoomPortionIndex,
        tickerWatchlistSearchInput,
        setTickerWatchlistSearchInput,
        cutOffDateChart,
        lastPrice,
        prevClosePrice,
        setPrevClosePrice,
        marketClosingPrice,
        setMarketClosingPrice,
        longName,
        setLongName,
        volume,
        setVolume,
        marketTimes,
        setMarketTimes,
        unrealizedTickersPrice,
        displayDataParsed,
        currencyTicker,
        conversionModes,
        searchThisTicker,
        setSearchThisTicker,
        testPrices,
        setTestPrices,
        holdingDayChange,
        holdingMarketValue,
        unrealizedTickers,
        lastClosePriceDelta,
        lastClosePriceCompare,
        currencyMultiplier,
        tradesDataArray,
        chartDataSelected,
        zoomFilteredChartData,
        selectedTickerCurrency,
        findFilteredIndex,
        bulkText,
        setBulkText,
        uploadArray,
        setUploadArray,
        uploadArrayError,
        setUploadArrayError,
        replaceMode,
        setReplaceMode,
        tradeTemplate,
        fieldObj,
        setFieldObj,
        pinCodeLife,
        pinCode,
        setPinCode,
        privateMode,
        setPrivateMode,
        adminMode,
        setAdminMode,
        isMarketTime,
        marketStatus,
        numFormat,
        getMaxRangeForInterval,
        handleChangeParam,
        handleSearchTicker,
        handlePinCode,
        recordUpToDateTickerInfo,
        addDataToState,
        handleGetChartData,
        resetZoomIndex,
        getHoldingInfo,
        getSplitMultiplier,
        isRunning,
        ignoreTokenLimit,
        setIgnoreTokenLimit,
        marketRefreshReady,
        serverActive,
        runServiceHealthCheck,
        checkHealthIfServiceDown,
      }}
    >
      {children}
    </AppContext.Provider>
  );
};

export const useAppContext = (): AppContextValue => {
  const context = useContext(AppContext);
  if (!context) {
    throw new Error("useAppContext must be used within AppProvider");
  }
  return context;
};
